import { resolveTaxSummaryBlock } from '../../../new-elite-landing/taxSummaryDisplay'
import type { AdviceCard } from '../../types/adviceCards.types'
import { useElitePlansLanding } from '../../ElitePlansLandingContext'
import {
  DEFAULT_ADVICE_CARD_GRADIENT,
  DEFAULT_ADVICE_DOTS_COLOR,
  ElitePlansAdviceCard
} from '../ElitePlansAdviceCard'
import { ORANGE_ADVICE_CARD_THEME, resolveAdviceCardText } from './adviceCardContent'
import { AdviceCardPlanIcon } from './AdviceCardPlanIcon'
import { buildAllocationPlanAdviceCardDisplay } from './allocationPlanAdviceCardDisplay'
import { buildIdealPortfolioAdviceCardDisplay } from './idealPortfolioAdviceCardDisplay'
import { buildIdealSIPPlanAdviceCardDisplay } from './idealSipPlanAdviceCardDisplay'
import { buildLumpSumInvestmentPlanAdviceCardDisplay } from './lumpSumInvestmentPlanAdviceCardDisplay'
import { buildRebalancePlanAdviceCardDisplay } from './rebalancePlanAdviceCardDisplay'
import { buildRegularToDirectPlanAdviceCardDisplay } from './regularToDirectPlanAdviceCardDisplay'
import { buildSIPPlanAdviceCardDisplay } from './sipPlanAdviceCardDisplay'
import { buildTaxSummaryAdviceCardDisplay } from './taxSummaryAdviceCardDisplay'
import { isStrategyFindCopyStatus } from './adviceCardStrategyStatusDisplay'
import { hasAdviceCardCta, postAdviceCardCta } from './postAdviceCardCta'
import { useRefetchStatusAfterStrategyQuestionnaire } from '../../hooks/useRefetchStatusAfterStrategyQuestionnaire'

export function renderAdviceCard(card: AdviceCard) {
  switch (card.id) {
    case 'rebalance-plan':
      return <RebalancePlanAdviceCard card={card} />
    case 'allocation-plan':
      return <AllocationPlanAdviceCard card={card} />
    case 'ideal-portfolio':
      return <IdealPortfolioAdviceCard card={card} />
    case 'sip-plan':
      return <SIPPlanAdviceCard card={card} />
    case 'ideal-sip-plan':
      return <IdealSIPPlanAdviceCard card={card} />
    case 'lump-sum-investment-plan':
      return <LumpSumInvestmentPlanAdviceCard card={card} />
    case 'regular-to-direct-plan':
      return <RegularToDirectPlanAdviceCard card={card} />
    case 'tax-summary':
      return <TaxSummaryAdviceCard card={card} />
    default:
      return <GenericAdviceCard card={card} />
  }
}

function RebalancePlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { eliteInsights, missedGains, fundsToRebalanceFallback } = useElitePlansLanding()
  const rebalanceCta = eliteInsights?.rebalanceCtaRequest
  // Hide when eliteInsights.rebalanceCtaRequest is absent
  if (!hasAdviceCardCta(rebalanceCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Rebalance Plan',
    body: 'Exit underperforming funds and switch to stronger alternatives'
  })
  const display = buildRebalancePlanAdviceCardDisplay(eliteInsights, {
    missedGains,
    fundsToRebalanceFallback
  })
  const cardId = card?.id ?? 'rebalance-plan'

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      statusLabel={display.statusLabel}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postAdviceCardCta(rebalanceCta)}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function AllocationPlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { elitePrimeAdvicePlanData } = useElitePlansLanding()
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const planData = elitePrimeAdvicePlanData.allocationPlanData
  // Hide when elitePrimeInsights.allocationPlanData is absent
  if (!planData) return null

  const strategyData = elitePrimeAdvicePlanData.strategyData
  const planCta = planData?.cta
  if (!hasAdviceCardCta(planCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Allocation Plan',
    body: 'Optimise portfolio allocation as per your life stage & risk appetite'
  })
  const display = buildAllocationPlanAdviceCardDisplay({ planData, strategyData })
  const cardId = card?.id ?? 'allocation-plan'
  const opensQuestionnaire = isStrategyFindCopyStatus(strategyData?.status)

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postStrategyCta(planCta, { opensQuestionnaire })}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function IdealPortfolioAdviceCard({ card }: { card?: AdviceCard }) {
  const { elitePrimeAdvicePlanData } = useElitePlansLanding()
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const planData = elitePrimeAdvicePlanData.idealPortfolioData
  // Hide when elitePrimeInsights.idealPortfolioData is absent
  if (!planData) return null

  const strategyData = elitePrimeAdvicePlanData.strategyData
  const planCta = planData?.cta
  if (!hasAdviceCardCta(planCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Ideal Portfolio',
    body: "Know how your portfolio looks when it's personalised to you"
  })
  const display = buildIdealPortfolioAdviceCardDisplay({ planData, strategyData })
  const cardId = card?.id ?? 'ideal-portfolio'
  const opensQuestionnaire = isStrategyFindCopyStatus(strategyData?.status)

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postStrategyCta(planCta, { opensQuestionnaire })}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function SIPPlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { elitePrimeAdvicePlanData } = useElitePlansLanding()
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const planData = elitePrimeAdvicePlanData.allocationSipPlanData
  // Hide when elitePrimeInsights.allocationSipPlanData is absent
  if (!planData) return null

  const strategyData = elitePrimeAdvicePlanData.strategyData
  const planCta = planData?.cta
  if (!hasAdviceCardCta(planCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'SIP Plan',
    body: 'Invest in a mix of funds every month to maintain ideal allocation'
  })
  const display = buildSIPPlanAdviceCardDisplay({ planData, strategyData })
  const cardId = card?.id ?? 'sip-plan'
  const opensQuestionnaire = isStrategyFindCopyStatus(strategyData?.status)

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postStrategyCta(planCta, { opensQuestionnaire })}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function IdealSIPPlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { elitePrimeAdvicePlanData } = useElitePlansLanding()
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const planData = elitePrimeAdvicePlanData.idealSipData
  // Hide when elitePrimeInsights.idealSipData is absent
  if (!planData) return null

  const strategyData = elitePrimeAdvicePlanData.strategyData
  const planCta = planData?.cta
  if (!hasAdviceCardCta(planCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Ideal SIP Plan',
    body: "Know how your SIP portfolio looks when it's personalised to you"
  })
  const display = buildIdealSIPPlanAdviceCardDisplay({ planData, strategyData })
  const cardId = card?.id ?? 'ideal-sip-plan'
  const opensQuestionnaire = isStrategyFindCopyStatus(strategyData?.status)

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postStrategyCta(planCta, { opensQuestionnaire })}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function LumpSumInvestmentPlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { status, elitePrimeAdvicePlanData } = useElitePlansLanding()
  const { postStrategyCta } = useRefetchStatusAfterStrategyQuestionnaire()
  const cta = status?.elitePrimeInsights?.allocationInvestmentStrategyCta
  const strategyData = elitePrimeAdvicePlanData.strategyData
  const allocationPlanData = elitePrimeAdvicePlanData.allocationPlanData
  const copy = resolveAdviceCardText(card, {
    title: 'Lump Sum Investment Plan',
    body: 'Find the right funds to add fresh investments as per your strategy'
  })
  const display = buildLumpSumInvestmentPlanAdviceCardDisplay({ strategyData, allocationPlanData })
  const cardId = card?.id ?? 'lump-sum-investment-plan'
  const opensQuestionnaire = isStrategyFindCopyStatus(strategyData?.status)
  // CTA may be null — still show status; only tappable when CTA is present
  const onClick = hasAdviceCardCta(cta)
    ? () => postStrategyCta(cta, { opensQuestionnaire })
    : undefined

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={onClick}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function RegularToDirectPlanAdviceCard({ card }: { card?: AdviceCard }) {
  const { eliteInsights } = useElitePlansLanding()
  const regularToDirect = eliteInsights?.regularToDirect
  const regularPlanAlert = eliteInsights?.regularPlanAlert
  const regularToDirectCta = regularToDirect?.regularToDirectCtaRequest
  const regularPlanAlertCta = regularPlanAlert?.cta
  const fundsCount = regularToDirect?.fundsCount ?? 0
  const extraCommissions = regularPlanAlert?.extraCommissions ?? 0
  const hasRegularToDirectInsight = Boolean(regularToDirectCta?.url) && fundsCount > 0
  const hasRegularPlanAlertInsight = Boolean(regularPlanAlertCta?.url) && extraCommissions > 0
  // Hide without regularToDirect and/or regularPlanAlert insight keys
  if (!hasRegularToDirectInsight && !hasRegularPlanAlertInsight) return null

  const adviceCta = regularToDirectCta?.url ? regularToDirectCta : regularPlanAlertCta
  if (!adviceCta?.url) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Regular to Direct Plan',
    body: 'Switch to funds with lower expense ratios, save more returns'
  })
  const display = buildRegularToDirectPlanAdviceCardDisplay(eliteInsights)
  const cardId = card?.id ?? 'regular-to-direct-plan'

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postAdviceCardCta(adviceCta)}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function TaxSummaryAdviceCard({ card }: { card?: AdviceCard }) {
  const { eliteInsights, status } = useElitePlansLanding()
  const taxSummary = resolveTaxSummaryBlock(eliteInsights?.taxSummary, eliteInsights?.taxSummaryCta)
  // Hide when taxSummary / legacy taxSummaryCta is absent
  if (!taxSummary) return null
  const taxCta = taxSummary.cta
  if (!hasAdviceCardCta(taxCta)) return null

  const copy = resolveAdviceCardText(card, {
    title: 'Tax Summary',
    body: 'See how much taxes you owe, and utilise your tax benefits efficiently'
  })
  const display = buildTaxSummaryAdviceCardDisplay(eliteInsights, status?.isUserElitePrime ?? false)
  const cardId = card?.id ?? 'tax-summary'

  return (
    <ElitePlansAdviceCard
      {...ORANGE_ADVICE_CARD_THEME}
      {...copy}
      accentColor={card?.accentColor ?? ORANGE_ADVICE_CARD_THEME.accentColor}
      backgroundGradient={card?.backgroundGradient ?? ORANGE_ADVICE_CARD_THEME.backgroundGradient}
      dotsColor={card?.dotsColor ?? ORANGE_ADVICE_CARD_THEME.dotsColor}
      borderColor={card?.borderColor ?? ORANGE_ADVICE_CARD_THEME.borderColor}
      stats={display.stats}
      insightTone={display.insightTone}
      onClick={() => postAdviceCardCta(taxCta)}
      icon={<AdviceCardPlanIcon cardId={cardId} />}
    />
  )
}

function GenericAdviceCard({ card }: { card: AdviceCard }) {
  const title = card.title ?? ''
  const body = card.body ?? ''
  const stats = (card.stats ?? []).map((stat) => ({
    value: stat.value,
    label: stat.label ?? ''
  }))

  return (
    <ElitePlansAdviceCard
      title={title}
      body={body}
      stats={stats}
      accentColor={card.accentColor}
      backgroundGradient={card.backgroundGradient ?? DEFAULT_ADVICE_CARD_GRADIENT}
      dotsColor={card.dotsColor ?? DEFAULT_ADVICE_DOTS_COLOR}
      borderColor={card.borderColor}
      icon={<AdviceCardPlanIcon cardId={card.id} />}
    />
  )
}
